import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, TextField, Card, Typography, Grid, MenuItem } from '@material-ui/core';
import { Formik } from 'formik';
import * as yup from 'yup';
import { createSubscriptionTierApi, updateSubscriptionTierApi } from 'api';
import NumberFormat from 'react-number-format';


const EditSubscriptionTier = ({tier, setTier}) => {
  const dispatch = useDispatch();

  const onChangePlansCount = (e, field, values, setValues, setFieldValue) => {
    const {value} = e.target;
    // update dynamic form
    const plans = [];
    const numberOfPlans = value || 0;
    const previousNumber = parseInt(field.value || '0');
    const autoTitle = ['Monthly', 'Semi-Annual', 'Annual'];
    const autoPeriod = [1, 6, 12];
    const autoDiscount = [0, 5, 10];

    if (previousNumber < numberOfPlans) {
      for (let i = previousNumber; i < numberOfPlans; i++) {
        plans.push(
          {
            title: autoTitle[i],
            price: Number(values.monthlyTierPrice),
            priceDueToday: ((Number(values.monthlyTierPrice) - ((Number(values.monthlyTierPrice) * autoDiscount[i]) / 100)) * autoPeriod[i]).toFixed(2),
            savings: autoDiscount[i],
            months: autoPeriod[i] //describe monthly periodicity: every 1 month, every 6 months, etc.
          }
        );
      }
    } else {
      for (let i = previousNumber; i >= numberOfPlans; i--) {
        plans.splice(i, 1);
      }
    }

    setValues({ ...values, plans });
    setFieldValue(field, value);
  };

  const generatePlans = (plans = [], plansCount, monthlyTierPrice) => {
    if (plans?.length) {
      return plans;
    }

    const autoTitle = ['Monthly', 'Semi-Annual', 'Annual'];
    const autoPeriod = [1, 6, 12];
    const autoDiscount = [0, 5, 10];

    for (let i = 0; i < plansCount; i++) {
      plans.push(
        {
          title: autoTitle[i],
          price: Number(monthlyTierPrice),
          priceDueToday: ((Number(monthlyTierPrice) - ((Number(monthlyTierPrice) * autoDiscount[i]) / 100)) * autoPeriod[i]).toFixed(2),
          savings: autoDiscount[i],
          months: autoPeriod[i] //describe monthly periodicity: every 1 month, every 6 months, etc.
        }
      );
    }

    return plans;
  };

  const initPlansCount = tier?.plans?.length ? String(tier?.plans?.length) : '3';
  const initMonthlyTierPrice = tier?.plans?.[0]?.price || 0;

  return (
    <Card className="p-8">
      <Formik
        initialValues={{
          _id: tier._id,
          title: tier.title || '',
          plansCount: initPlansCount,
          monthlyTierPrice: initMonthlyTierPrice,
          plans: generatePlans(tier.plans, initPlansCount, initMonthlyTierPrice)
        }}
        enableReinitialize
        validationSchema={
          yup.object({
            title: yup
              .string()
              .required('Field is required'),
            monthlyTierPrice: yup
              .number()
              .required('Field is required'),
            plansCount: yup
              .number()
              .required('Field is required'),
            plans: yup
              .array().of(
                yup.object().shape({
                  title: yup
                    .string()
                    .required('Field is required'),
                  price: yup
                    .number()
                    .required('Field is required'),
                  priceDueToday: yup
                    .number()
                    .required('Field is required'),
                  savings: yup
                    .number()
                    .required('Field is required'),
                  months: yup
                    .number()
                    .required('Field is required'),
                }
                )
              )
          })
        }
        onSubmit={async (values) => {
          if (values._id) {
            await updateSubscriptionTierApi({dispatch, tier: values});
          } else {
            await createSubscriptionTierApi({dispatch, tier: values});
          }

          setTier(null);
        }}
      >
        {({
          values,
          errors,
          touched,
          isValid,
          dirty,
          setValues,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue
        }) => {
          return (
            <form
              onSubmit={handleSubmit}
            >
              <TextField
                className="my-2"
                name="title"
                label="Tier Plan Title"
                required
                fullWidth
                value={values.title}
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.title && Boolean(errors.title)}
                helperText={touched.title && errors.title}
              />
              <NumberFormat
                allowNegative={false}
                thousandSeparator
                className="my-2"
                prefix="$ "
                variant="outlined"
                name="monthlyTierPrice"
                label="Plan Start Price"
                decimalScale={2}
                defaultValue={values.monthlyTierPrice}
                onValueChange={(val) => {
                  const price = val.floatValue;
                  const updatedPlans = values.plans.map((plan) => {
                    const months = Number(plan.months);
                    const priceDueToday = ((price - ((price * plan.savings) / 100)) * months).toFixed(2);
                    return {...plan, priceDueToday, price };
                  });

                  setValues((values) => ({...values, plans: updatedPlans, monthlyTierPrice: price }));
                }}
                customInput={TextField}
                error={touched.monthlyTierPrice && Boolean(errors.monthlyTierPrice)}
                helperText={touched.monthlyTierPrice && errors.monthlyTierPrice}
              />
              <TextField
                className="my-2"
                name="plansCount"
                fullWidth
                required
                select
                label="Count of Subscription Plans"
                variant="outlined"
                value={values.plansCount}
                onChange={(event) => {
                  onChangePlansCount(event, 'plansCount', values, setValues, setFieldValue);
                }}
                onBlur={handleBlur}
                error={touched.plansCount && Boolean(errors.plansCount)}
                helperText={touched.plansCount && errors.plansCount}
              >
                <MenuItem value="1" disabled>1</MenuItem>
                <MenuItem value="2" disabled>2</MenuItem>
                <MenuItem value="3">3</MenuItem>
                <MenuItem value="4" disabled>4</MenuItem>
              </TextField>
              {!!values.plans.length && <Typography variant="h4" className="mt-8 mb-4">Available Subscription Plans: </Typography>}
              <div className="flex items-center">
                {values.plans.map((plan, idx) => {
                  console.log(plan);
                  const planErrors = (errors.plans?.length && errors.plans[idx]) || {};
                  const planTouched = (touched.plans?.length && touched.plans[idx]) || {};
                  return (
                    <Card
                      key={`plan-${idx}`}
                      className="w-full p-4 m-4"
                      elevation={25}
                    >
                      <TextField
                        className="my-2"
                        name={`plans.${idx}.title`}
                        label="Subscription Plan Title"
                        required
                        fullWidth
                        value={values.plans[idx].title}
                        variant="outlined"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={planTouched.title && planErrors.title}
                        helperText={planTouched.title && planErrors.title}
                      />
                      <NumberFormat
                        allowNegative={false}
                        thousandSeparator
                        className="my-2"
                        prefix="$ "
                        variant="outlined"
                        fullWidth
                        decimalScale={2}
                        customInput={TextField}
                        name={`plans.${idx}.price`}
                        label="Plan Start Price"
                        value={values.plans[idx].price}
                        onValueChange={(val) => {
                          const price = val.floatValue;

                          if (values.plans[idx].price === price) {
                            return;
                          }

                          const priceDueToday = ((price - ((price * values.plans[idx].savings) / 100)) * values.plans[idx].months).toFixed(2);
                          setValues((values) => ({...values, plans: values.plans.map((p, pIdx) => pIdx === idx ? ({...p, priceDueToday, price}) : p)}));
                        }}
                        error={planTouched.price && planErrors.price}
                        helperText={planTouched.price && planErrors.price}
                      />
                      <NumberFormat
                        allowNegative={false}
                        thousandSeparator
                        className="my-2"
                        variant="outlined"
                        fullWidth
                        decimalScale={2}
                        customInput={TextField}
                        name={`plans.${idx}.months`}
                        label="Monthly Paid Period"
                        value={values.plans[idx].months}
                        onValueChange={(val) => {
                          const months = val.floatValue;
                          const price = Number(values.plans[idx].price);
                          const priceDueToday = ((price - ((price * values.plans[idx].savings) / 100)) * months).toFixed(2);
                          // handleChange(event);
                          setValues((values) => ({...values, plans: values.plans.map((p, pIdx) => pIdx === idx ? ({...p, priceDueToday, months}) : p)}));
                        }}
                        error={planTouched.months && planErrors.months}
                        helperText={planTouched.months && planErrors.months}
                      />
                      <NumberFormat
                        allowNegative={false}
                        thousandSeparator
                        className="my-2"
                        variant="outlined"
                        fullWidth
                        decimalScale={2}
                        customInput={TextField}
                        name={`plans.${idx}.savings`}
                        label="Percent Discount"
                        value={values.plans[idx].savings}
                        suffix=" %"
                        onValueChange={(val) => {
                          const savings = val.floatValue;
                          const price = Number(values.plans[idx].price);
                          const priceDueToday = ((price - ((price * savings) / 100)) * values.plans[idx].months).toFixed(2);
                          // handleChange(event);
                          setValues((values) => ({...values, plans: values.plans.map((p, pIdx) => pIdx === idx ? ({...p, priceDueToday, savings}) : p)}));
                        }}
                        error={planTouched.savings && planErrors.savings}
                        helperText={planTouched.savings && planErrors.savings}
                      />
                      <NumberFormat
                        allowNegative={false}
                        thousandSeparator
                        className="my-2"
                        prefix="$ "
                        variant="outlined"
                        fullWidth
                        decimalScale={2}
                        customInput={TextField}
                        value={values.plans[idx].priceDueToday}
                        name={`plans.${idx}.priceDueToday`}
                        label="Plan Price Due Today"
                        onValueChange={(val) => {
                          const priceDueToday = val.floatValue;

                          if (Number(values.plans[idx].priceDueToday) === priceDueToday) {
                            return;
                          }

                          setValues((values) => ({...values, plans: values.plans.map((p, pIdx) => pIdx === idx ? ({...p, priceDueToday}) : p)}));
                        }}
                        error={planTouched.priceDueToday && planErrors.priceDueToday}
                        helperText={planTouched.priceDueToday && planErrors.priceDueToday}
                      />
                      <NumberFormat
                        allowNegative={false}
                        thousandSeparator
                        className="my-2"
                        variant="outlined"
                        fullWidth
                        customInput={TextField}
                        name={`plans.${idx}.promoSkusLimit`}
                        label="Promo SKUS limit"
                        value={values.plans[idx].promoSkusLimit}
                        onChange={handleChange}
                      />
                      <NumberFormat
                        allowNegative={false}
                        thousandSeparator
                        className="my-2"
                        variant="outlined"
                        fullWidth
                        customInput={TextField}
                        name={`plans.${idx}.promosPerMonthLimit`}
                        label="Promos per month limit"
                        value={values.plans[idx].promosPerMonthLimit}
                        onChange={handleChange}
                      />
                    </Card>
                  );
                })}
              </div>
              <div>
                <Typography variant="h4" className="mb-4 mt-8">Summary</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    Tier Plan Title
                  </Grid>
                  <Grid item xs={12} sm={4} className="font-bold">
                    {values.title}
                  </Grid>
                  {values.plans.map((plan, planId) => {
                    const { title, savings, priceDueToday } = plan;
                    return (
                      <Grid key={`summary-plan-${planId}`} item container xs={12}>
                        <Grid item xs={7} sm={4} >
                          {title}{' '}{savings != 0 && `(${savings}% discount)`}
                        </Grid>
                        <Grid item xs={7} sm={2} className="font-bold">
                          <NumberFormat
                            thousandSeparator
                            className="my-2"
                            prefix="$ "
                            variant="outlined"
                            displayType="text"
                            decimalScale={2}
                            value={priceDueToday}
                            customInput={TextField}
                          />
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>
              </div>
              <div className="flex items-center justify-between mt-8">
                <Button
                  variant="contained"
                  onClick={() => setTier(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!(isValid && dirty)}
                  color="primary"
                  variant="contained"
                >
                  {values._id ? 'Save Tier Plan' : 'Create Tier Plan'}
                </Button>
              </div>
            </form>
          );
        }
        }
      </Formik>

    </Card>
  );
};


export default EditSubscriptionTier;
